// Tester Compensation page — under Testers nav.
//
// One row per (tester, account) with:
//   - Tester display
//   - Account display_name
//   - Current balance
//   - FRT Tester Fee
//   - Payment Status (inline editable)
//   - Payment Date (required when status = 'Paid')
//   - Contract Expiration Date
//   - Action Required / Pending Closing / Suspended badges
//
// Inline edits go through PATCH /accounts/:id/payment-status; clicking
// "Action Required" opens the renewal modal which calls
// POST /accounts/:id/contract-renewal.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { accounts as accountsApi, lookups, testerCompensation } from '@/lib/api';
import { formatBalance, formatTransaction } from '@/lib/format';
import { useMe } from '@/hooks/useMe';
import type { TesterCompensationRow } from '@frt/shared-types';

export function TesterCompensationPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';

  const { data: comp, isLoading, error } = useQuery({
    queryKey: ['tester-compensation'],
    queryFn: () => testerCompensation.list(),
  });
  const { data: paymentStatuses } = useQuery({
    queryKey: ['lookups', 'payment-status-options'],
    queryFn: () => lookups.paymentStatusOptions.list(),
  });

  const [renewalFor, setRenewalFor] = useState<TesterCompensationRow | null>(null);

  const groupedByTester = useMemo(() => {
    const map = new Map<number, { display: string; rows: TesterCompensationRow[] }>();
    for (const r of comp?.data ?? []) {
      let g = map.get(r.tester_id);
      if (!g) {
        g = { display: r.tester_display, rows: [] };
        map.set(r.tester_id, g);
      }
      g.rows.push(r);
    }
    return [...map.values()].sort((a, b) => a.display.localeCompare(b.display));
  }, [comp]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Tester Compensation</h1>
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <Card>
          <CardBody>
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Tester</th>
                  <th className="py-2 pr-3">Account</th>
                  <th className="py-2 pr-3 text-right">Balance</th>
                  <th className="py-2 pr-3 text-right">Fee</th>
                  <th className="py-2 pr-3">Payment Status</th>
                  <th className="py-2 pr-3">Payment Date</th>
                  <th className="py-2 pr-3">Contract Expires</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {groupedByTester.flatMap((g) =>
                  g.rows.map((r, i) => (
                    <CompRow
                      key={`${r.tester_id}-${r.account_id}`}
                      row={r}
                      paymentStatuses={paymentStatuses?.data ?? []}
                      canEdit={canEdit}
                      isFirstForTester={i === 0}
                      onActionRequired={() => setRenewalFor(r)}
                      onSaved={() =>
                        queryClient.invalidateQueries({ queryKey: ['tester-compensation'] })
                      }
                    />
                  )),
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {renewalFor && (
        <ContractRenewalModal
          row={renewalFor}
          paymentStatuses={paymentStatuses?.data ?? []}
          onClose={() => setRenewalFor(null)}
          onResolved={() => {
            setRenewalFor(null);
            void queryClient.invalidateQueries({ queryKey: ['tester-compensation'] });
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }}
        />
      )}
    </div>
  );
}

function CompRow({
  row,
  paymentStatuses,
  canEdit,
  isFirstForTester,
  onActionRequired,
  onSaved,
}: {
  row: TesterCompensationRow;
  paymentStatuses: Array<{ id: number; name: string; active: boolean }>;
  canEdit: boolean;
  isFirstForTester: boolean;
  onActionRequired: () => void;
  onSaved: () => void;
}) {
  const [statusId, setStatusId] = useState<number | ''>(row.payment_status_id ?? '');
  const [date, setDate] = useState<string>(row.payment_date ?? '');
  const [savingError, setSavingError] = useState<string | null>(null);

  const selectedName = paymentStatuses.find((s) => s.id === statusId)?.name;
  const requiresDate = selectedName === 'Paid';

  const save = useMutation({
    mutationFn: () =>
      accountsApi.updatePaymentStatus(row.account_id, {
        payment_status_id: statusId || null,
        payment_date: date || null,
      }),
    onSuccess: () => {
      setSavingError(null);
      onSaved();
    },
    onError: (e: unknown) => setSavingError((e as Error).message),
  });

  const dirty =
    statusId !== (row.payment_status_id ?? '') || date !== (row.payment_date ?? '');

  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-2 pr-3">
        {isFirstForTester ? (
          <Link
            to="/testers/$id"
            params={{ id: String(row.tester_id) }}
            className="text-brand-700 hover:underline"
          >
            {row.tester_display}
          </Link>
        ) : (
          ''
        )}
      </td>
      <td className="py-2 pr-3">
        <Link
          to="/accounts/$id"
          params={{ id: String(row.account_id) }}
          className="text-brand-700 hover:underline font-mono text-xs"
        >
          {row.account_display_name}
        </Link>
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{formatBalance(row.current_balance)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">
        {row.frt_tester_fee == null ? '—' : formatTransaction(row.frt_tester_fee)}
      </td>
      <td className="py-2 pr-3">
        <select
          value={statusId}
          disabled={!canEdit}
          onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}
          className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
        >
          <option value="">—</option>
          {paymentStatuses.filter((s) => s.active).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 pr-3">
        <Input
          type="date"
          value={date}
          disabled={!canEdit}
          onChange={(e) => setDate(e.target.value)}
          required={requiresDate}
        />
      </td>
      <td className="py-2 pr-3 text-slate-600">{row.contract_expiration_date ?? '—'}</td>
      <td className="py-2 pr-3 text-right whitespace-nowrap">
        {row.attention_state === 'action_required' && (
          <Button size="sm" variant="destructive" onClick={onActionRequired}>
            Action Required
          </Button>
        )}
        {row.attention_state === 'pending_closing' && (
          <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
            Pending Closing
          </span>
        )}
        {row.attention_state === 'suspended' && (
          <span className="rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700">
            Suspended
          </span>
        )}
        {dirty && canEdit && (
          <Button
            size="sm"
            className="ml-2"
            onClick={() => save.mutate()}
            disabled={save.isPending || (requiresDate && !date)}
          >
            Save
          </Button>
        )}
        {savingError && <div className="text-xs text-red-600 mt-1">{savingError}</div>}
      </td>
    </tr>
  );
}

function ContractRenewalModal({
  row,
  paymentStatuses,
  onClose,
  onResolved,
}: {
  row: TesterCompensationRow;
  paymentStatuses: Array<{ id: number; name: string; active: boolean }>;
  onClose: () => void;
  onResolved: () => void;
}) {
  const [decision, setDecision] = useState<'renew' | 'close' | 'suspend' | ''>('');
  const [signedDate, setSignedDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [termMonths, setTermMonths] = useState<string>('12');
  const [fee, setFee] = useState<string>(
    row.frt_tester_fee != null ? String(row.frt_tester_fee) : '',
  );
  const [statusId, setStatusId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      accountsApi.contractRenewal(row.account_id, {
        decision: decision as 'renew' | 'close' | 'suspend',
        ...(decision === 'renew'
          ? {
              contract_signed: true,
              contract_signed_date: signedDate,
              contract_term_months: Number(termMonths),
              frt_tester_fee: fee ? Number(fee) : null,
              payment_status_id: statusId || null,
            }
          : {}),
      }),
    onSuccess: () => {
      setError(null);
      onResolved();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-lg">
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Contract Renewal Required</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>
            Contract for{' '}
            <span className="font-mono">{row.account_display_name}</span> has
            reached its expiration date. How would you like to proceed?
          </p>
          <label className="block">
            <span className="block text-slate-600">Decision</span>
            <select
              value={decision}
              onChange={(e) =>
                setDecision(e.target.value as 'renew' | 'close' | 'suspend' | '')
              }
              className="block w-full rounded border border-slate-300 bg-white px-2 py-2"
            >
              <option value="">— pick one —</option>
              <option value="renew">Yes — Renew the contract</option>
              <option value="close">No — Close the contract</option>
              <option value="suspend">Suspend — Defer the decision</option>
            </select>
          </label>
          {decision === 'renew' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-slate-600">Contract Signed Date *</span>
                <Input
                  type="date"
                  value={signedDate}
                  onChange={(e) => setSignedDate(e.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="block text-slate-600">Contract Term (months) *</span>
                <Input
                  type="number"
                  min={1}
                  value={termMonths}
                  onChange={(e) => setTermMonths(e.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="block text-slate-600">FRT Tester Fee ($)</span>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={fee}
                  onChange={(e) => setFee(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="block text-slate-600">Payment Status</span>
                <select
                  value={statusId}
                  onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}
                  className="block w-full rounded border border-slate-300 bg-white px-2 py-2"
                >
                  <option value="">—</option>
                  {paymentStatuses.filter((s) => s.active).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {error && <div className="text-red-600">{error}</div>}
        </div>
        <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!decision || submit.isPending}
            onClick={() => submit.mutate()}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
